import { Link, Stack, Text, useMediaQuery } from '@ltvco/refresh-lib/theme';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { ReportColumnContainer } from 'components/ChatBV/components';

import { WebSourceAnnotation } from './WebSources';

type SourcesColumnProps = {
  onClose: () => void;
  sources: WebSourceAnnotation[];
  currentWidth: string;
};

export const SourcesColumn = ({
  onClose,
  sources,
  currentWidth,
}: SourcesColumnProps) => {
  const isMobileBreakpoint = useMediaQuery('(max-width: 1024px)');

  return (
    <ReportColumnContainer
      direction="column"
      spacing={2}
      minWidth={currentWidth}
      width="40%"
      height="98%"
      data-testid="sources-column-container"
      sx={{
        ...(isMobileBreakpoint
          ? {
              animation: 'fadeInScale 0.4s ease-out',
              '@keyframes fadeInScale': {
                '0%': {
                  transform: 'scale(0.95)',
                  opacity: 0,
                },
                '100%': {
                  transform: 'scale(1)',
                  opacity: 1,
                },
              },
            }
          : {
              animation: 'slideInFromRight 0.5s ease-out',
              '@keyframes slideInFromRight': {
                '0%': {
                  transform: 'translateX(100%)',
                  opacity: 0,
                },
                '100%': {
                  transform: 'translateX(0)',
                  opacity: 1,
                },
              },
            }),
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{
          position: 'sticky',
          top: 0,
          backgroundColor: 'background.paper',
          zIndex: 1,
          padding: '16px 20px',
        }}
      >
        <Text variant="body1" fontWeight="bold" color="text.primary">
          Sources ({sources.length})
        </Text>
        <CloseIcon
          onClick={onClose}
          sx={{ color: 'text.secondary', cursor: 'pointer' }}
        />
      </Stack>

      <Stack spacing={2} sx={{ padding: '0 20px 20px' }}>
        {sources.map((source, index) => (
          <Link
            key={`${source.url}-${index}`}
            href={source.url}
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
              padding: 2,
              borderRadius: '8px',
              textDecoration: 'none',
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
              <Text
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  overflow: 'hidden',
                  '&:hover': {
                    color: '#0052FF',
                  },
                }}
              >
                {source.title}
              </Text>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <LinkIcon
                  sx={{
                    fontSize: '14px',
                    color: 'text.secondary',
                    flexShrink: 0,
                  }}
                />
                <Text
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {source.url}
                </Text>
              </Stack>
            </Stack>
            <OpenInNewIcon
              sx={{
                fontSize: '18px',
                color: 'text.secondary',
                flexShrink: 0,
              }}
            />
          </Link>
        ))}
      </Stack>
    </ReportColumnContainer>
  );
};
